// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { cartPageContainerStyles } from "@tps/Overrides/Widgets/Cart/CartPageContainer";
import color from "@tps/XDL/Utilities/color";
import xScreen from "@tps/XDL/Utilities/xScreen";
import { css } from "styled-components";

interface CheckoutSharedStyles {
    container?: InjectableCss;
    leftSection?: InjectableCss;
    rightSection?: InjectableCss;
    addressesAndOrderDetailsSection?: InjectableCss;
    paymentsAndPromotionSection?: InjectableCss;
    productsSection?: InjectableCss;
    cartTotalSection?: InjectableCss;
    separator?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    orderSummaryWrapper?: InjectableCss;
    orderSummaryDivider?: InjectableCss;
    subSectionTitle?: TypographyPresentationProps;
    billingInfoAndOrderDetailsSeparator?: InjectableCss;
    notifyMsg?: InjectableCss;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
}

export const checkoutSharedStyles: CheckoutSharedStyles = {
    container: {
        css: css`
            display: flex;
            column-gap: 22px;
            ${xScreen("lte", "md")} {
                column-gap: 20px;
            }
            ${xScreen("lte", 767)} {
                flex-wrap: wrap;
                row-gap: 52px;
            }
        `,
    },
    cartTotalSection: {
        css: css`
            width: 317px;

            ${xScreen("lte", "md")} {
                width: 358px;
            }

            ${xScreen("lte", 767)} {
                width: 100%;
            }
            position: sticky;
            top: 68px;
            ${xScreen("lte", "md")} {
                position: initial;
            }
        `,
    },
    separator: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            width: 100%;
            height: 1px;
            margin: 32px 0;
        `,
    },
    rightSection: {
        css: css`
            display: flex;
            flex-direction: column;
            row-gap: 16px;
            ${xScreen("lte", 767)} {
                width: 100%;
            }
            @media print {
                margin-top: 52px;
            }
        `,
    },
    sectionTitle: {
        variant: "h2",
        css: css`
            margin-bottom: 16px;
        `,
    },
    orderSummaryWrapper: {
        css: css`
            flex-direction: column;
            row-gap: 8px;
        `,
    },
    orderSummaryDivider: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            width: 100%;
            height: 1px;
            margin: 4px 0;
        `,
    },
    leftSection: {
        css: css`
            flex-grow: 1;
        `,
    },
    subSectionTitle: {
        variant: "p",
        lineHeight: "22px",
        size: 16,
        weight: "bold",
        css: css`
            margin-bottom: 12px;
            @media print {
                font-size: 16px;
            }
        `,
    },
    billingInfoAndOrderDetailsSeparator: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            width: 100%;
            height: 1px;
            margin: 17px 0 16px;
        `,
    },
    notifyMsg: {
        css: css`
            margin-bottom: 0;
        `,
    },
    headingAndTextStyles: {
        extendedStyles: {
            wrapper: {
                css: css`
                    display: flex;
                    flex-direction: column;
                    row-gap: 12px;
                `,
            },
            heading: {
                lineHeight: "22px",
                weight: "bold",
                size: 16,
                transform: "capitalize",
            },
            text: {
                lineHeight: "22px",
                weight: "normal",
                size: 14,
            },
        },
    },
};
